import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Users, Search, Trash2, Shield, Crown, ChevronDown, BarChart3, RefreshCw, Mail, CheckCircle, AlertCircle, Clock, Eye, MousePointerClick, UserPlus, Activity, ShoppingBag, Zap, X, Radio, Euro } from 'lucide-react';
import { toast } from 'sonner';
import { TrafficSummary } from '../../components/admin/TrafficSummary';
import { Link } from 'react-router-dom';

export default function AdminDashboard() {
  const { api } = useAuth();
  const [tab, setTab] = useState('users');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [expandedUser, setExpandedUser] = useState(null);
  const [workerStatus, setWorkerStatus] = useState(null);
  const [leadStats, setLeadStats] = useState(null);
  const [activity24h, setActivity24h] = useState(null);
  const [drillDown, setDrillDown] = useState(null); // { metric, label, color, items, loading }
  const [overview, setOverview] = useState(null);
  const [liveEvents, setLiveEvents] = useState([]);
  const [trafficSummary, setTrafficSummary] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [onlineNow, setOnlineNow] = useState(null);
  const [recentSales, setRecentSales] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsR, usersR, ovR] = await Promise.all([
        api('get', '/api/admin/ceo-stats'),
        api('get', `/api/admin/radar-users?search=${encodeURIComponent(search)}`),
        api('get', '/api/admin/overview/metrics').catch(() => ({ data: null })),
      ]);
      setStats(statsR.data);
      setUsers(usersR.data.users || []);
      if (ovR.data) setOverview(ovR.data);
    } catch (e) {
      toast.error('Fout bij laden dashboard');
    }
    setLoading(false);
  }, [api, search]);

  const loadWorkerStatus = useCallback(async () => {
    try {
      const [wr, lr, ar, tr, tfR, onR, dashR] = await Promise.all([
        api('get', '/api/admin/leads/worker-status'),
        api('get', '/api/admin/leads'),
        api('get', '/api/admin/24h-activity'),
        api('get', '/api/admin/traffic/live?limit=12').catch(() => ({ data: { events: [] } })),
        api('get', '/api/admin/traffic/funnel?days=7').catch(() => ({ data: null })),
        api('get', '/api/admin/traffic/online-now').catch(() => ({ data: null })),
        api('get', '/api/admin/dashboard').catch(() => ({ data: null })),
      ]);
      setWorkerStatus(wr.data);
      setLeadStats(lr.data.stats || {});
      setActivity24h(ar.data);
      setLiveEvents(tr.data?.events || []);
      setTrafficSummary(tfR.data);
      setOnlineNow(onR.data);
      setDashboardStats(dashR.data?.stats || null);
      setRecentSales(dashR.data?.recent_sales || []);
      setLastUpdated(new Date());
    } catch { /* ignore */ }
  }, [api]);

  useEffect(() => { loadData(); loadWorkerStatus(); }, [loadData, loadWorkerStatus]);
  useEffect(() => {
    const interval = setInterval(loadWorkerStatus, 10000);
    return () => clearInterval(interval);
  }, [loadWorkerStatus]);

  const updateRole = async (userId, newRole, opts = {}) => {
    setActionLoading(userId);
    try {
      const body = { role: newRole, ...opts };
      const r = await api('post', `/api/admin/radar-users/${userId}/role`, body);
      const label = r.data?.plan_state ? `${ROLE_LABELS[newRole] || newRole} (${r.data.plan_state})` : (ROLE_LABELS[newRole] || newRole);
      toast.success(`Rol bijgewerkt: ${label}`);
      loadData();
    } catch (e) {
      toast.error('Fout bij bijwerken rol');
    }
    setActionLoading(null);
  };

  const rescanCustomer = async (userId, email) => {
    if (!window.confirm(`Re-scan uitvoeren voor ${email}? Oude scan + opdrachten worden vervangen.`)) return;
    setActionLoading(userId);
    try {
      const startResp = await api('post', `/api/admin/customers/${userId}/rescan`);
      const jobId = startResp.data?.job_id;
      if (!jobId) {
        toast.error('Re-scan kon niet starten');
        setActionLoading(null);
        return;
      }
      toast.info('Re-scan gestart in de achtergrond — kan ~30-90s duren');

      // Poll voor voltooiing — max 3 minuten
      const startTime = Date.now();
      const maxMs = 180_000;
      const interval = 4000;
      let done = false;
      while (!done && Date.now() - startTime < maxMs) {
        await new Promise(r => setTimeout(r, interval));
        try {
          const statusResp = await api('get', `/api/admin/customers/${userId}/rescan/status/${jobId}`);
          const s = statusResp.data?.status;
          if (s === 'completed') {
            const opCount = statusResp.data?.opdrachten_count || 0;
            const score = statusResp.data?.overall_score;
            const dur = statusResp.data?.duration_seconds;
            toast.success(`Re-scan voltooid · ${opCount} opdrachten · score ${score} · ${dur}s`);
            loadData();
            done = true;
            break;
          } else if (s === 'failed') {
            toast.error(`Re-scan mislukt: ${statusResp.data?.error || 'onbekende fout'}`);
            done = true;
            break;
          }
        } catch (e) {
          // Polling failure — keep trying
        }
      }
      if (!done) {
        toast.warning('Re-scan duurt langer dan verwacht — controleer de gebruikersrij later.');
        loadData();
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Re-scan mislukt';
      toast.error(msg);
    }
    setActionLoading(null);
  };

  const deleteUser = async (userId, email) => {
    if (!window.confirm(`Weet u zeker dat u ${email} wilt verwijderen? Alle data wordt permanent verwijderd.`)) return;
    setActionLoading(userId);
    try {
      await api('delete', `/api/admin/radar-users/${userId}`);
      toast.success('Gebruiker verwijderd');
      loadData();
    } catch (e) {
      toast.error('Fout bij verwijderen');
    }
    setActionLoading(null);
  };

  const openDrillDown = async (metric, label, color) => {
    setDrillDown({ metric, label, color, items: [], loading: true });
    try {
      const r = await api('get', `/api/admin/24h-activity/recent/${metric}?limit=50`);
      setDrillDown({ metric, label, color, items: r.data.items || [], loading: false, timestamp_field: r.data.timestamp_field });
    } catch (e) {
      toast.error('Kon details niet laden');
      setDrillDown(null);
    }
  };

  const closeDrillDown = () => setDrillDown(null);

  const ROLE_LABELS = {
    radar_free: 'Gratis Scan',
    radar_paid: 'Connected Self-Service · €14,95/mnd',
    beheer_paid: 'Managed Premium · €995,95 → €2.495,95',
    admin: 'Admin',
  };

  const ROLE_COLORS = {
    radar_free: { bg: '#f1f5f9', text: '#64748b' },
    radar_paid: { bg: '#dbeafe', text: '#2563eb' },
    beheer_paid: { bg: '#fef3c7', text: '#d97706' },
    admin: { bg: '#fce4ec', text: '#c62828' },
  };

  return (
    <div data-testid="admin-dashboard" style={{ maxWidth: '1200px', margin: '0 auto', overflow: 'hidden' }}>
      <TrafficSummary />
      {/* Header */}
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
        <div>
          <h1 style={{ fontFamily: "'Sora', sans-serif", fontWeight: 800, color: 'white', marginBottom: '4px' }}
              className="text-xl md:text-2xl">
            Admin Dashboard
          </h1>
          <p style={{ color: '#94a3b8', fontSize: '14px' }}>Beheer klanten, rollen en platform overzicht</p>
        </div>
        {lastUpdated && (
          <div data-testid="admin-last-updated" style={{
            display: 'inline-flex', alignItems: 'center', gap: '8px',
            background: 'rgba(34,197,94,0.12)', color: '#22c55e', borderRadius: '20px',
            padding: '6px 14px', fontSize: '11px', fontWeight: 700,
          }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
            Live · bijgewerkt {lastUpdated.toLocaleTimeString('nl-NL', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
          </div>
        )}
      </div>

      {/* Live Overzicht — Sales + Realtime bezoekers (NIEUW) */}
      <div data-testid="live-overview" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', marginBottom: '18px' }}>
        <div style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)', borderRadius: '12px', padding: '14px 18px', color: 'white' }}>
          <p style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.85 }}>Live MRR</p>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: '26px', fontWeight: 800, marginTop: '2px' }} data-testid="live-mrr">
            €{Math.round(dashboardStats?.total_mrr || 0).toLocaleString('nl-NL')}
          </p>
          <p style={{ fontSize: '11px', marginTop: '2px', opacity: 0.85 }}>
            {dashboardStats?.paid_users || 0} betalende klanten · totaal €{Math.round(dashboardStats?.total_revenue || 0).toLocaleString('nl-NL')} omzet
          </p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)', borderRadius: '12px', padding: '14px 18px', color: 'white' }}>
          <p style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.85, display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} /> Nu online
          </p>
          <p style={{ fontFamily: "'Sora',sans-serif", fontSize: '26px', fontWeight: 800, marginTop: '2px' }} data-testid="online-now-count">
            {onlineNow?.online_now || 0}
          </p>
          <p style={{ fontSize: '11px', marginTop: '2px', opacity: 0.85 }}>
            {onlineNow?.authenticated || 0} ingelogd · {onlineNow?.anonymous || 0} bezoekers · laatste 5 min
          </p>
        </div>
        <div style={{ background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)', borderRadius: '12px', padding: '14px 18px', color: 'white' }}>
          <p style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.85 }}>Laatste sale</p>
          {recentSales.length > 0 ? (
            <>
              <p style={{ fontFamily: "'Sora',sans-serif", fontSize: '22px', fontWeight: 800, marginTop: '2px' }} data-testid="last-sale-amount">
                €{Number(recentSales[0]?.amount || 0).toFixed(2)}
              </p>
              <p style={{ fontSize: '11px', marginTop: '2px', opacity: 0.9 }}>
                {recentSales[0]?.user_email || 'Onbekend'} · {(recentSales[0]?.created_at || '').slice(0, 10)}
              </p>
            </>
          ) : (
            <p style={{ fontSize: '13px', marginTop: '4px', opacity: 0.75 }}>Nog geen verkopen</p>
          )}
        </div>
      </div>

      {/* Recente sales lijst (compact) */}
      {recentSales.length > 0 && (
        <div data-testid="recent-sales-list" style={{ background: 'rgba(15,23,42,0.5)', border: '1px solid #1e293b', borderRadius: '12px', padding: '14px 18px', marginBottom: '18px' }}>
          <p style={{ color: 'white', fontSize: '13px', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Euro size={14} color="#22c55e" /> Recente verkopen
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {recentSales.slice(0, 5).map((s, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: '#cbd5e1', padding: '6px 0', borderBottom: i < 4 ? '1px solid #1e293b' : 'none' }}>
                <span style={{ flex: 1 }}>{s.user_email || s.user_name || 'Onbekend'}</span>
                <span style={{ color: '#94a3b8', minWidth: 180 }}>{s.name || s.package_id}</span>
                <span style={{ fontWeight: 700, color: '#22c55e', minWidth: 60, textAlign: 'right' }}>€{Number(s.amount || 0).toFixed(2)}</span>
                <span style={{ color: '#64748b', minWidth: 90, textAlign: 'right', fontSize: '11px' }}>{(s.created_at || '').slice(5, 16).replace('T', ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Operations Overview Banner (Batch 2) */}
      {overview && (
        <div data-testid="ops-overview-banner" style={{ background: 'linear-gradient(135deg, #D4A017 0%, #b88a13 100%)', borderRadius: '14px', padding: '18px 22px', marginBottom: '18px', color: '#0f172a' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
            <div>
              <p style={{ fontSize: '10px', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.1em', opacity: 0.7 }}>Operations Snapshot</p>
              <p style={{ fontFamily: "'Sora',sans-serif", fontSize: '24px', fontWeight: 800, marginTop: '2px' }}>
                MRR {overview.revenue?.mrr_ex_vat_formatted} excl. btw
              </p>
              <p style={{ fontSize: '12px', marginTop: '4px' }}>
                {overview.customers?.total_active} actieve klanten · {overview.customers?.self_service_14_95} self-service · {overview.customers?.managed_launch_995_95} launch · {overview.customers?.managed_recurring_2495_95} recurring
              </p>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, auto)', gap: '12px' }}>
              <OvStat label="Nieuw vandaag" value={overview.leads?.new_today || 0} />
              <OvStat label="In sequence" value={overview.leads?.in_sequence || 0} />
              <OvStat label="Due nu" value={overview.leads?.due_now || 0} highlight={(overview.leads?.due_now || 0) > 0} />
              <OvStat label="Overdue follow-up" value={overview.leads?.overdue_followups || 0} highlight={(overview.leads?.overdue_followups || 0) > 0} />
              <OvStat label="Failed payments 30d" value={overview.payments?.failed_30d || 0} />
              <OvStat label="Outbox failed" value={overview.automation?.outbox_failed || 0} highlight={(overview.automation?.outbox_failed || 0) > 0} />
            </div>
          </div>
          <div style={{ marginTop: '14px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <Link to="/admin/crm" data-testid="banner-crm" style={{ background: '#0f172a', color: 'white', padding: '7px 14px', borderRadius: '8px', textDecoration: 'none', fontSize: '11px', fontWeight: 700 }}>CRM Pipeline →</Link>
            <Link to="/admin/reconcile" data-testid="banner-reconcile" style={{ background: '#0f172a', color: 'white', padding: '7px 14px', borderRadius: '8px', textDecoration: 'none', fontSize: '11px', fontWeight: 700 }}>Reconcile</Link>
            <Link to="/admin/invoices" style={{ background: 'rgba(15,23,42,0.15)', color: '#0f172a', padding: '7px 14px', borderRadius: '8px', textDecoration: 'none', fontSize: '11px', fontWeight: 700 }}>Facturen</Link>
            <Link to="/admin/templates" style={{ background: 'rgba(15,23,42,0.15)', color: '#0f172a', padding: '7px 14px', borderRadius: '8px', textDecoration: 'none', fontSize: '11px', fontWeight: 700 }}>Email templates</Link>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      {stats && (
        <div className="dash-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '14px', marginBottom: '16px' }}>
          {[
            { label: 'Totaal gebruikers', value: stats.total_users || users.length, color: '#2563eb' },
            { label: 'Radar betaald', value: stats.paid_users || 0, color: '#22c55e' },
            { label: 'Scans gedaan', value: stats.total_scans || 0, color: '#D4A017' },
            { label: 'MRR', value: `€${(stats.total_mrr || 0).toFixed(0)}`, color: '#8b5cf6' },
          ].map(kpi => (
            <div key={kpi.label} style={{ background: 'white', borderRadius: '14px', padding: '16px', border: '1px solid #f1f5f9' }}>
              <p style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '4px' }}>{kpi.label}</p>
              <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: kpi.color }}>{kpi.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Live Email Worker Status */}
      {leadStats && (
        <div data-testid="email-worker-live" style={{
          background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '14px',
          padding: '18px 22px', marginBottom: '24px', color: 'white',
        }}>
          <div className="admin-email-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Mail size={18} style={{ color: '#D4A017' }} />
              <span style={{ fontSize: '14px', fontWeight: 800 }}>Email Campagne — Live</span>
              {workerStatus?.worker_running && (
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '5px',
                  background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: '20px',
                  padding: '3px 10px', fontSize: '11px', fontWeight: 700,
                }}>
                  <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
                  Worker actief
                </span>
              )}
            </div>
            <Link to="/admin/leads" data-testid="go-to-leads-btn" style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              background: '#D4A017', color: '#0f172a', borderRadius: '8px', padding: '7px 14px',
              fontSize: '12px', fontWeight: 700, textDecoration: 'none',
            }}>
              Leads beheren →
            </Link>
          </div>
          <div className="admin-kpi-5" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }}>
            {[
              { label: 'Totaal leads', value: leadStats.total || 0, color: '#f8fafc', icon: Users },
              { label: 'Verstuurd', value: leadStats.sent || 0, color: '#22c55e', icon: CheckCircle },
              { label: 'In afwachting', value: leadStats.pending || 0, color: '#d97706', icon: Clock },
              { label: 'Fouten', value: leadStats.errors || 0, color: '#ef4444', icon: AlertCircle },
              { label: 'Geconverteerd', value: leadStats.converted || 0, color: '#818cf8', icon: Crown },
            ].map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} style={{
                  background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px',
                  textAlign: 'center', border: '1px solid rgba(255,255,255,0.06)',
                }}>
                  <Icon size={14} style={{ color: s.color, marginBottom: '4px' }} />
                  <p style={{ fontSize: '22px', fontWeight: 900, color: s.color, margin: '2px 0' }}>{s.value}</p>
                  <p style={{ fontSize: '9px', color: 'rgba(255,255,255,0.45)', margin: 0, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.label}</p>
                </div>
              );
            })}
          </div>
          {(leadStats.total || 0) > 0 && (
            <div style={{ marginTop: '12px' }}>
              <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '6px', height: '6px', overflow: 'hidden' }}>
                <div style={{
                  height: '100%', borderRadius: '6px',
                  background: 'linear-gradient(90deg, #22c55e, #D4A017)',
                  width: `${Math.round(((leadStats.sent || 0) / (leadStats.total || 1)) * 100)}%`,
                  transition: 'width 0.5s ease',
                }} />
              </div>
              <p style={{ fontSize: '10px', color: 'rgba(255,255,255,0.35)', margin: '6px 0 0', textAlign: 'right' }}>
                {Math.round(((leadStats.sent || 0) / (leadStats.total || 1)) * 100)}% voltooid — ververst elke 10s
              </p>
            </div>
          )}
        </div>
      )}

      {/* Live 24-uur Activiteit */}
      {activity24h && (
        <div data-testid="activity-24h-widget" style={{
          background: 'white', borderRadius: '14px', padding: '18px 22px',
          marginBottom: '24px', border: '1px solid #f1f5f9',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: 'rgba(212,160,23,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Activity size={17} style={{ color: '#D4A017' }} />
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: 800, color: '#0f172a', margin: 0 }}>Live 24-uur Activiteit</p>
                <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>Wat er vandaag gebeurt op uw platform</p>
              </div>
            </div>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(34,197,94,0.1)', color: '#16a34a', borderRadius: '20px', padding: '4px 10px', fontSize: '11px', fontWeight: 700 }}>
              <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
              Live
            </span>
          </div>
          <div className="activity-24h-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '10px' }}>
            {[
              { metric: 'sent', label: 'Verstuurd', value: activity24h.metrics.emails_sent, color: '#0f172a', icon: Mail },
              { metric: 'opened', label: 'Geopend', value: activity24h.metrics.emails_opened, color: '#2563eb', icon: Eye, hint: `${activity24h.rates.open_rate}%` },
              { metric: 'clicked', label: 'Geklikt', value: activity24h.metrics.emails_clicked, color: '#8b5cf6', icon: MousePointerClick, hint: `${activity24h.rates.click_rate}%` },
              { metric: 'registrations', label: 'Registraties', value: activity24h.metrics.new_registrations, color: '#16a34a', icon: UserPlus },
              { metric: 'scans', label: 'Scans', value: activity24h.metrics.scans_done, color: '#D4A017', icon: Zap },
              { metric: 'conversions', label: 'Conversies', value: activity24h.metrics.conversions, color: '#7c3aed', icon: Crown, hint: `${activity24h.rates.conversion_rate}%` },
              { metric: 'orders', label: 'Bestellingen', value: activity24h.metrics.new_orders, color: '#dc2626', icon: ShoppingBag },
            ].map(s => {
              const Icon = s.icon;
              const clickable = s.value > 0;
              return (
                <button
                  key={s.label}
                  data-testid={`activity-24h-${s.label.toLowerCase()}`}
                  onClick={() => clickable && openDrillDown(s.metric, s.label, s.color)}
                  disabled={!clickable}
                  style={{
                    background: '#fafafa', borderRadius: '10px', padding: '12px 10px',
                    textAlign: 'center', border: '1px solid #f1f5f9',
                    cursor: clickable ? 'pointer' : 'default',
                    transition: 'all 0.15s',
                    opacity: clickable ? 1 : 0.6,
                  }}
                  onMouseEnter={(e) => { if (clickable) { e.currentTarget.style.background = '#fef9e7'; e.currentTarget.style.borderColor = '#D4A017'; } }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#fafafa'; e.currentTarget.style.borderColor = '#f1f5f9'; }}
                >
                  <Icon size={14} style={{ color: s.color, marginBottom: '4px' }} />
                  <p style={{ fontSize: '22px', fontWeight: 900, color: s.color, margin: '2px 0', lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: '9px', color: '#94a3b8', margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.label}</p>
                  {s.hint && <p style={{ fontSize: '9px', color: s.color, margin: '2px 0 0', fontWeight: 700 }}>{s.hint}</p>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Live Traffic Ticker (laatste events + funnel summary) */}
      {(liveEvents.length > 0 || trafficSummary) && (
        <div data-testid="admin-live-ticker" style={{
          background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '14px',
          padding: '18px 22px', marginBottom: '24px', color: 'white',
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px', flexWrap: 'wrap', gap: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <Radio size={18} style={{ color: '#D4A017' }} />
              <span style={{ fontSize: '14px', fontWeight: 800 }}>Live Traffic Ticker</span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: 'rgba(34,197,94,0.15)', color: '#22c55e', borderRadius: '20px', padding: '3px 10px', fontSize: '10px', fontWeight: 700 }}>
                <span style={{ width: '5px', height: '5px', borderRadius: '50%', background: '#22c55e', animation: 'pulse 1.5s infinite' }} />
                LIVE · ververst 10s
              </span>
            </div>
            <Link to="/admin/traffic" data-testid="goto-traffic-btn" style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              background: '#D4A017', color: '#0f172a', borderRadius: '8px', padding: '6px 12px',
              fontSize: '11px', fontWeight: 700, textDecoration: 'none',
            }}>
              Volledige funnel →
            </Link>
          </div>

          {trafficSummary && (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '8px', marginBottom: '14px' }} className="admin-funnel-mini">
              {[
                { label: 'Bezoekers 7d', value: trafficSummary.visits?.unique_visitors || 0, color: '#cbd5e1' },
                { label: 'Scans', value: trafficSummary.scan_complete?.unique || 0, color: '#0ea5e9' },
                { label: 'Registraties', value: trafficSummary.register?.unique || 0, color: '#22c55e' },
                { label: 'Paid', value: trafficSummary.paid?.unique || 0, color: '#D4A017' },
                { label: 'Visit → Paid', value: `${(trafficSummary.conversion_rates?.overall_visit_to_paid || 0).toFixed(1)}%`, color: '#D4A017' },
              ].map(s => (
                <div key={s.label} style={{ background: 'rgba(255,255,255,0.04)', borderRadius: '8px', padding: '10px', textAlign: 'center', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <p style={{ fontSize: '20px', fontWeight: 900, color: s.color, margin: 0, lineHeight: 1 }}>{s.value}</p>
                  <p style={{ fontSize: '9px', color: 'rgba(255,255,255,0.5)', margin: '4px 0 0', textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{s.label}</p>
                </div>
              ))}
            </div>
          )}

          {liveEvents.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', maxHeight: '200px', overflowY: 'auto' }}>
              {liveEvents.slice(0, 8).map((ev, idx) => {
                const KIND_COLOR = { scan_complete: '#0ea5e9', register: '#22c55e', paid: '#D4A017', scan_start: '#94a3b8', checkout_started: '#f59e0b' };
                const KIND_LABEL = { scan_complete: 'Scan voltooid', register: 'Registratie', paid: 'Betaald', scan_start: 'Scan gestart', checkout_started: 'Checkout' };
                const color = KIND_COLOR[ev.kind] || '#94a3b8';
                const ts = ev.ts ? new Date(ev.ts).toLocaleString('nl-NL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '';
                return (
                  <div key={idx} style={{
                    display: 'flex', alignItems: 'center', gap: '10px',
                    padding: '6px 10px', background: 'rgba(255,255,255,0.03)', borderRadius: '6px',
                    borderLeft: `3px solid ${color}`,
                  }}>
                    <span style={{ fontSize: '10px', fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: '0.04em', minWidth: '90px' }}>
                      {KIND_LABEL[ev.kind] || ev.kind}
                    </span>
                    <span style={{ fontSize: '11px', color: '#cbd5e1', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {ev.email || ev.company_name || ev.path || `visitor ${ev.visitor_id}`}
                      {ev.utm_source ? ` · via ${ev.utm_source}` : ''}
                    </span>
                    <span style={{ fontSize: '10px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{ts}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', borderBottom: '2px solid #f1f5f9', paddingBottom: '0' }}>
        {[
          { id: 'users', label: 'Gebruikers', icon: Users },
          { id: 'stats', label: 'Statistieken', icon: BarChart3 },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', fontSize: '13px',
              fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? '#D4A017' : '#64748b',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${tab === t.id ? '#D4A017' : 'transparent'}`, marginBottom: '-2px',
            }}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* Users Tab */}
      {tab === 'users' && (
        <div>
          {/* Search */}
          <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
            <div style={{ flex: 1, position: 'relative' }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
              <input
                data-testid="user-search"
                value={search} onChange={e => setSearch(e.target.value)}
                placeholder="Zoek op naam of email..."
                style={{
                  width: '100%', padding: '10px 12px 10px 36px', borderRadius: '10px',
                  border: '1px solid #e2e8f0', fontSize: '13px', outline: 'none',
                }}
              />
            </div>
            <button onClick={loadData} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 14px',
              borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white',
              fontSize: '13px', cursor: 'pointer', color: '#64748b',
            }}>
              <RefreshCw size={14} /> Vernieuwen
            </button>
          </div>

          {/* Users Table */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Laden...</div>
          ) : (
            <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9', overflow: 'hidden', overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ background: '#f8fafc', borderBottom: '1px solid #f1f5f9' }}>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Gebruiker</th>
                    <th style={{ padding: '10px 14px', textAlign: 'left', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Rol</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Score</th>
                    <th style={{ padding: '10px 14px', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Opdrachten</th>
                    <th style={{ padding: '10px 14px', textAlign: 'right', fontSize: '11px', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase' }}>Acties</th>
                  </tr>
                </thead>
                <tbody>
                  {users.map(u => (
                    <React.Fragment key={u.id}>
                      <tr style={{ borderBottom: '1px solid #f8fafc', transition: 'background 0.1s' }}
                        onMouseEnter={e => e.currentTarget.style.background = '#fafafa'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <td style={{ padding: '12px 14px' }}>
                          <div>
                            <p style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>{u.name || u.google_name || u.email?.split('@')[0]}</p>
                            <p style={{ fontSize: '11px', color: '#94a3b8' }}>{u.email}</p>
                          </div>
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            display: 'inline-block', padding: '3px 10px', borderRadius: '8px', fontSize: '11px', fontWeight: 600,
                            background: ROLE_COLORS[u.role]?.bg || '#f1f5f9', color: ROLE_COLORS[u.role]?.text || '#64748b',
                          }}>
                            {ROLE_LABELS[u.role] || u.role}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>
                            {u.scan?.score ?? '—'}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'center' }}>
                          <span style={{ fontSize: '13px', color: '#64748b' }}>{u.opdrachten_count || 0}</span>
                        </td>
                        <td style={{ padding: '12px 14px', textAlign: 'right' }}>
                          <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                            <button data-testid={`expand-${u.id}`}
                              onClick={() => setExpandedUser(expandedUser === u.id ? null : u.id)}
                              style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer' }}>
                              <ChevronDown size={14} style={{ color: '#64748b', transform: expandedUser === u.id ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />
                            </button>
                            {u.role !== 'admin' && (
                              <button data-testid={`delete-${u.id}`}
                                onClick={() => deleteUser(u.id, u.email)}
                                disabled={actionLoading === u.id}
                                style={{ padding: '6px 8px', borderRadius: '8px', border: '1px solid #fecaca', background: '#fef2f2', cursor: 'pointer' }}>
                                <Trash2 size={14} style={{ color: '#ef4444' }} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                      {expandedUser === u.id && (
                        <tr>
                          <td colSpan={5} style={{ padding: '0 14px 14px', background: '#fafafa' }}>
                            <div style={{ padding: '14px', background: 'white', borderRadius: '12px', border: '1px solid #f1f5f9' }}>
                              <p style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '10px' }}>Plan toekennen</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '12px' }}>
                                <button
                                  data-testid={`role-radar_free-${u.id}`}
                                  onClick={() => updateRole(u.id, 'radar_free')}
                                  disabled={u.role === 'radar_free' || actionLoading === u.id}
                                  style={{ padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, border: `2px solid ${u.role === 'radar_free' ? '#D4A017' : '#e2e8f0'}`, background: u.role === 'radar_free' ? 'rgba(212,160,23,0.08)' : 'white', color: u.role === 'radar_free' ? '#D4A017' : '#374151', cursor: u.role === 'radar_free' ? 'default' : 'pointer' }}
                                >
                                  Gratis Scan {u.role === 'radar_free' && <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: '4px' }}>(huidig)</span>}
                                </button>
                                <button
                                  data-testid={`role-radar_paid-${u.id}`}
                                  onClick={() => updateRole(u.id, 'radar_paid')}
                                  disabled={u.role === 'radar_paid' || actionLoading === u.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, border: `2px solid ${u.role === 'radar_paid' ? '#D4A017' : '#e2e8f0'}`, background: u.role === 'radar_paid' ? 'rgba(212,160,23,0.08)' : 'white', color: u.role === 'radar_paid' ? '#D4A017' : '#374151', cursor: u.role === 'radar_paid' ? 'default' : 'pointer' }}
                                >
                                  <Crown size={12} /> Connected Self-Service · €14,95 {u.role === 'radar_paid' && <span style={{ fontSize: '10px', color: '#94a3b8' }}>(huidig)</span>}
                                </button>
                                <button
                                  data-testid={`role-beheer_paid-launch-${u.id}`}
                                  onClick={() => updateRole(u.id, 'beheer_paid', { billing_phase: 'launch_month' })}
                                  disabled={actionLoading === u.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, border: `2px solid ${(u.role === 'beheer_paid' && u.billing_phase === 'launch_month') ? '#D4A017' : '#e2e8f0'}`, background: (u.role === 'beheer_paid' && u.billing_phase === 'launch_month') ? 'rgba(212,160,23,0.08)' : 'white', color: (u.role === 'beheer_paid' && u.billing_phase === 'launch_month') ? '#D4A017' : '#374151', cursor: 'pointer' }}
                                >
                                  <Shield size={12} /> Managed · Launch (€995,95)
                                </button>
                                <button
                                  data-testid={`role-beheer_paid-recurring-${u.id}`}
                                  onClick={() => updateRole(u.id, 'beheer_paid', { billing_phase: 'recurring' })}
                                  disabled={actionLoading === u.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 600, border: `2px solid ${(u.role === 'beheer_paid' && u.billing_phase === 'recurring') ? '#D4A017' : '#e2e8f0'}`, background: (u.role === 'beheer_paid' && u.billing_phase === 'recurring') ? 'rgba(212,160,23,0.08)' : 'white', color: (u.role === 'beheer_paid' && u.billing_phase === 'recurring') ? '#D4A017' : '#374151', cursor: 'pointer' }}
                                >
                                  <Shield size={12} /> Managed · Recurring (€2.495,95)
                                </button>
                              </div>

                              <p style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', marginBottom: '6px' }}>Scan acties</p>
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                                <button
                                  data-testid={`rescan-${u.id}`}
                                  onClick={() => rescanCustomer(u.id, u.email)}
                                  disabled={actionLoading === u.id}
                                  style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 14px', borderRadius: '10px', fontSize: '12px', fontWeight: 700, border: 'none', background: '#0f172a', color: 'white', cursor: 'pointer', opacity: actionLoading === u.id ? 0.6 : 1 }}
                                >
                                  <RefreshCw size={12} /> {actionLoading === u.id ? 'Bezig...' : 'Voer verse scan uit'}
                                </button>
                              </div>

                              {u.scan && (
                                <div style={{ marginTop: '10px', padding: '8px 12px', background: '#f8fafc', borderRadius: '8px' }}>
                                  <p style={{ fontSize: '11px', color: '#94a3b8' }}>
                                    Bedrijf: <strong style={{ color: '#0f172a' }}>{u.scan.company_name || '—'}</strong> · Score: <strong style={{ color: '#0f172a' }}>{u.scan.score || '—'}</strong> · Opdrachten: <strong style={{ color: '#0f172a' }}>{u.opdrachten_count}</strong>
                                    {u.plan_state && <> · Plan: <strong style={{ color: '#0f172a' }}>{u.plan_state}</strong></>}
                                  </p>
                                </div>
                              )}
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                  {users.length === 0 && (
                    <tr>
                      <td colSpan={5} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8', fontSize: '14px' }}>
                        Geen gebruikers gevonden
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {tab === 'stats' && stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>Conversie Funnel</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Totaal gebruikers', value: stats.total_users || users.length },
                { label: 'Onboarding compleet', value: stats.onboarding_complete || 0 },
                { label: 'Scan uitgevoerd', value: stats.total_scans || 0 },
                { label: 'Betaalde klanten', value: stats.paid_users || 0 },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
          <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '12px' }}>Platform Status</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Actieve scans', value: stats.active_scans || 0 },
                { label: 'Gem. score', value: stats.avg_score ? stats.avg_score.toFixed(0) : '—' },
                { label: 'Totaal opdrachten', value: stats.total_opdrachten || 0 },
                { label: 'Concurrenten gescand', value: stats.total_competitors || 0 },
              ].map(item => (
                <div key={item.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                  <span style={{ fontSize: '13px', color: '#64748b' }}>{item.label}</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: '#0f172a' }}>{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Drill-down modal */}
      {drillDown && (
        <div
          data-testid="drill-down-modal"
          onClick={closeDrillDown}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
            padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '16px', padding: '24px',
              maxWidth: '720px', width: '100%', maxHeight: '80vh', overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '34px', height: '34px', borderRadius: '10px', background: `${drillDown.color}20`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Activity size={17} style={{ color: drillDown.color }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: 800, color: '#0f172a', margin: 0 }}>{drillDown.label}</h3>
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>Laatste 24 uur · max 50 resultaten</p>
                </div>
              </div>
              <button
                data-testid="close-drill-down"
                onClick={closeDrillDown}
                style={{ background: '#f1f5f9', border: 'none', borderRadius: '8px', padding: '6px', cursor: 'pointer' }}
              >
                <X size={16} style={{ color: '#64748b' }} />
              </button>
            </div>
            {drillDown.loading ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Laden…</div>
            ) : drillDown.items.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Geen items gevonden</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {drillDown.items.map((item, idx) => {
                  const ts = item[drillDown.timestamp_field];
                  const tsFormatted = ts ? new Date(ts).toLocaleString('nl-NL', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: 'short' }) : '';
                  return (
                    <div key={idx} style={{
                      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                      padding: '10px 14px', background: '#f8fafc', borderRadius: '10px',
                      border: '1px solid #f1f5f9', gap: '12px',
                    }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <p style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a', margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {item.email || item.name || item.id}
                        </p>
                        <p style={{ fontSize: '11px', color: '#64748b', margin: '2px 0 0' }}>
                          {item.bedrijfsnaam || item.user_email || item.user_name || item.website_url || item.website || '—'}
                          {item.amount && ` · €${item.amount}`}
                          {item.open_count != null && ` · ${item.open_count}x geopend`}
                          {item.click_count != null && ` · ${item.click_count}x geklikt`}
                        </p>
                      </div>
                      <span style={{ fontSize: '11px', color: '#94a3b8', whiteSpace: 'nowrap' }}>{tsFormatted}</span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const OvStat = ({ label, value, highlight }) => (
  <div style={{ background: highlight ? 'rgba(15,23,42,0.95)' : 'rgba(15,23,42,0.1)', color: highlight ? '#D4A017' : '#0f172a', padding: '8px 12px', borderRadius: '8px', textAlign: 'center', minWidth: '90px' }}>
    <p style={{ fontFamily: "'Sora',sans-serif", fontSize: '18px', fontWeight: 800, lineHeight: 1 }}>{value}</p>
    <p style={{ fontSize: '9px', fontWeight: 700, marginTop: '3px', textTransform: 'uppercase', letterSpacing: '0.06em', opacity: highlight ? 0.8 : 0.7 }}>{label}</p>
  </div>
);

